import { useRef, useEffect } from 'react';
import { useAppContext } from '../../components/app-context/app-context.tsx';
import { PopStateInterceptor } from '../../services/history-service.ts';

// Adapted original from https://usehooks.com/useEventListener/

/**
 * Hook for services.base.historyService.addPopStateInterceptor / removePopStateInterceptor.
 */
export const usePopStateInterceptor = (interceptor: PopStateInterceptor) => {
  const { appContext } = useAppContext();

  // Create a ref that stores handler
  const savedInterceptor = useRef(interceptor);

  // Update ref.current value if handler changes.
  // This allows our effect below to always get latest handler ...
  // ... without us needing to pass it in effect deps array ...
  // ... and potentially cause effect to re-run every render.
  useEffect(() => {
    savedInterceptor.current = interceptor;
  }, [interceptor]);

  useEffect(() => {
    // Create dummy interceptor that calls the interceptor stored in ref
    const dummyInterceptor: PopStateInterceptor = {
      shouldPopStateCancel: () => savedInterceptor.current.shouldPopStateCancel(),
      popStateCancelled: () => savedInterceptor.current.popStateCancelled()
    };

    // Add event interceptor
    appContext.historyService?.addPopStateInterceptor(dummyInterceptor);

    // Remove event interceptor on cleanup
    return () => {
      appContext.historyService?.removePopStateInterceptor(dummyInterceptor);
    };
  }, [appContext.historyService]);
};
